// IntakeForm.js
import React, { useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

// Animation variants
const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: 'easeOut' },
    },
};

const inputVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
        opacity: 1,
        x: 0,
        transition: { duration: 0.4, delay: i * 0.1, ease: 'easeOut' },
    }),
};

const hoverVariants = {
    hover: {
        scale: 1.05,
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
        transition: { duration: 0.3, ease: 'easeOut' },
    },
};

const notificationVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

const IntakeForm = () => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showNotification, setShowNotification] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm();
    const navigate = useNavigate();
    // const { token } = useContext(AuthContext);
    const token = localStorage.getItem('token');

    const onSubmit = async (data) => {
        setLoading(true);
        setErrorMessage('');
        setShowNotification(false);

        try {
            console.log('Form Data Submitted:', data);

            const apiUrl = `${process.env.REACT_APP_API_URL}/api/aiInsights`;
            console.log('API URL:', apiUrl);
            console.log('Token:', token);
            const response = await axios.post(apiUrl, data, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                timeout: 30000, // Increased timeout to 30 seconds
            });
            console.log('API Response:', response.data);

            const responseData = response.data;

            if (responseData && responseData.submissionId) {
                setShowNotification(true);
                reset();
                setTimeout(() => setShowNotification(false), 3000);

                navigate(`/results/${responseData.submissionId}`, { state: { aiInsights: responseData.aiInsights } });
            } else {
                console.error('Error: submissionId not received from the backend.');
                setErrorMessage('Failed to get submission ID. Please try again.');
                setTimeout(() => setErrorMessage(''), 5000);
            }

        } catch (error) {
            console.error('Submission Failed:', error);
            let message = 'An unexpected error occurred. Please try again later.';
            if (error.response) {
                message = error.response.data?.message || `Server error: ${error.response.status}`;
                if (error.response.status === 401 || error.response.status === 403) {
                    message = 'You need to be logged in to submit the form. Please log in and try again.';
                    navigate('/login');
                }
            } else if (error.code === 'ECONNABORTED') {
                message = 'Request timed out. Please ensure the backend server is running and try again.';
            } else if (error.request) {
                message = 'Network error: Unable to reach the server.';
            } else {
                message = error.message;
            }
            setErrorMessage(message);
            setTimeout(() => setErrorMessage(''), 5000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900 flex flex-col font-sans mt-10">
            <style>
                {`
                    .glow-button {
                        animation: glow 1.5s ease-in-out infinite alternate;
                    }
                    @keyframes glow {
                        from {
                            box-shadow: 0 0 5px rgba(37, 150, 190, 0.4), 0 0 10px rgba(37, 150, 190, 0.3);
                        }
                        to {
                            box-shadow: 0 0 15px rgba(37, 150, 190, 0.8), 0 0 25px rgba(37, 150, 190, 0.5);
                        }
                    }
                `}
            </style>

            <Header />

            {showNotification && (
                <motion.div
                    className="fixed top-4 right-4 bg-[#2596be] text-white px-4 py-2 rounded-lg shadow-lg z-50"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={notificationVariants}
                >
                    Form submitted successfully!
                </motion.div>
            )}

            {errorMessage && (
                <motion.div
                    className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={notificationVariants}
                >
                    {errorMessage}
                </motion.div>
            )}

            <motion.div
                variants={formVariants}
                initial="hidden"
                animate="visible"
                className="flex-grow flex items-center justify-center py-12"
            >
                <div className="max-w-2xl w-full p-8 bg-white rounded-lg shadow-md border border-gray-200">
                    <h2 className="text-3xl font-bold text-center text-[#2596be] mb-4">
                        Share Your Details
                    </h2>
                    <p className="text-gray-600 text-center mb-8">
                        Complete our simple questionnaire to receive your custom AI roadmap.
                    </p>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <motion.div variants={inputVariants} custom={0}>
                            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                                Company Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="companyName"
                                placeholder="Enter your company name"
                                {...register('companyName', { required: 'Company name is required' })}
                                className={`w-full px-4 py-2 rounded-lg border ${errors.companyName ? 'border-red-500' : 'border-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-[#2596be]`}
                                disabled={loading || isSubmitting}
                            />
                            {errors.companyName && (
                                <p className="text-red-500 text-xs mt-1">{errors.companyName.message}</p>
                            )}
                        </motion.div>

                        <motion.div variants={inputVariants} custom={1}>
                            <label htmlFor="sector" className="block text-sm font-medium text-gray-700 mb-1">
                                Industry Sector <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="sector"
                                placeholder="Enter your sector"
                                {...register('sector', { required: 'Industry sector is required' })}
                                className={`w-full px-4 py-2 rounded-lg border ${errors.sector ? 'border-red-500' : 'border-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-[#2596be]`}
                                disabled={loading || isSubmitting}
                            />
                            {errors.sector && (
                                <p className="text-red-500 text-xs mt-1">{errors.sector.message}</p>
                            )}
                        </motion.div>

                        <motion.div variants={inputVariants} custom={2}>
                            <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1">
                                Company Size <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="size"
                                {...register('size', { required: 'Company size is required' })}
                                className={`w-full px-4 py-2 rounded-lg border ${errors.size ? 'border-red-500' : 'border-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-[#2596be]`}
                                disabled={loading || isSubmitting}
                            >
                                <option value="">Select company size</option>
                                <option value="Startup">Startup</option>
                                <option value="Small">Small</option>
                                <option value="Medium">Medium</option>
                                <option value="Enterprise">Enterprise</option>
                                <option value="Corporate">Corporate</option>
                            </select>
                            {errors.size && (
                                <p className="text-red-500 text-xs mt-1">{errors.size.message}</p>
                            )}
                        </motion.div>

                        <motion.div variants={inputVariants} custom={3}>
                            <label htmlFor="goals" className="block text-sm font-medium text-gray-700 mb-1">
                                Key Goals <span className="text-gray-500">(Optional)</span>
                            </label>
                            <textarea
                                id="goals"
                                placeholder="Describe your business goals or challenges..."
                                {...register('goals')}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2596be]"
                                disabled={loading || isSubmitting}
                            />
                        </motion.div>

                        <motion.div variants={inputVariants} custom={4}>
                            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                                Company Website <span className="text-gray-500">(Optional)</span>
                            </label>
                            <input
                                type="url"
                                id="website"
                                placeholder="https://yourcompany.com"
                                {...register('website', {
                                    pattern: {
                                        value: /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})(\/.*)?$/,
                                        message: 'Please enter a valid URL',
                                    },
                                })}
                                className={`w-full px-4 py-2 rounded-lg border ${errors.website ? 'border-red-500' : 'border-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-[#2596be]`}
                                disabled={loading || isSubmitting}
                            />
                            {errors.website && (
                                <p className="text-red-500 text-xs mt-1">{errors.website.message}</p>
                            )}
                        </motion.div>

                        <motion.div variants={inputVariants} custom={5}>
                            <motion.button
                                type="submit"
                                disabled={loading || isSubmitting}
                                whileHover="hover"
                                variants={hoverVariants}
                                className={`w-full px-6 py-3 bg-[#2596be] text-white rounded-lg font-medium glow-button ${loading || isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#1e7a9b]'
                                    }`}
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <svg
                                            className="animate-spin h-4 w-4 mr-2 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Submitting...
                                    </span>
                                ) : (
                                    'Submit'
                                )}
                            </motion.button>
                        </motion.div>
                    </form>
                </div>
            </motion.div>
        </div>
    );
};

export default IntakeForm;